import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { getMangoApiClient } from '~/atoms/MangoApi'
import { useScheduleApi } from '~/utils/scheduleApiClient/scheduleApiClient'

import type {
  PaginatedScheduleList,
  Schedule,
  ScheduleCreate,
  ScheduleEvent,
  ScheduleEventCreate,
  ScheduleEventId,
  ScheduleEventUpdate,
  ScheduleHoliday,
  ScheduleId,
  ScheduleListParams,
  ScheduleRetrieve,
  ScheduleUpdate,
} from '@mangovoice/schedule'
import { buildDeleteScheduleError, isAssetUuid } from '@mangovoice/schedule'

interface ApiError {
  code: string
  message: string
  switchboardNames?: string[]
}

const INVALID_SCHEDULE_ROUTE_ERROR: ApiError = {
  code: 'fetch_schedule_error',
  message: '',
}

// Query Keys
export const SCHEDULE_QUERY_KEYS = {
  all: ['schedules'] as const,
  lists: () => [...SCHEDULE_QUERY_KEYS.all, 'list'] as const,
  list: (params: ScheduleListParams) =>
    [...SCHEDULE_QUERY_KEYS.lists(), params] as const,
  details: () => [...SCHEDULE_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: ScheduleId) => [...SCHEDULE_QUERY_KEYS.details(), id] as const,
  events: (scheduleId: ScheduleId) =>
    [...SCHEDULE_QUERY_KEYS.detail(scheduleId), 'events'] as const,
  legacyRoute: (legacyId: number) =>
    [...SCHEDULE_QUERY_KEYS.details(), 'legacy-route', legacyId] as const,
  holidays: () => ['schedule-holidays'] as const,
}

const legacyScheduleIdFromRoute = (id?: string): number | undefined => {
  if (!id || !/^\d+$/.test(id)) return undefined

  const legacyId = Number(id)
  return Number.isSafeInteger(legacyId) && legacyId > 0 ? legacyId : undefined
}

export const useResolveScheduleRouteId = (id?: string) => {
  const legacyId = legacyScheduleIdFromRoute(id)
  const assetId = isAssetUuid(id) ? id : undefined
  const invalidRouteError: ApiError | null =
    id && legacyId === undefined && assetId === undefined
      ? INVALID_SCHEDULE_ROUTE_ERROR
      : null
  const query = useQuery<string, ApiError>({
    queryKey: SCHEDULE_QUERY_KEYS.legacyRoute(legacyId ?? 0),
    queryFn: async () => {
      const { ok, data } = await getMangoApiClient().schedules.getSchedule(
        legacyId as number,
      )

      if (!ok || !data?.uuid) {
        return Promise.reject({
          code: 'fetch_schedule_error',
          message: `Failed to resolve legacy schedule ${legacyId}`,
        })
      }

      return data.uuid
    },
    enabled: legacyId !== undefined,
    retry: false,
  })

  return {
    ...query,
    error: invalidRouteError ?? query.error,
    isLegacyRoute: legacyId !== undefined,
    scheduleId: legacyId === undefined ? (assetId ?? '') : (query.data ?? ''),
  }
}

// List Schedules Hook
interface UseListSchedulesParams {
  queryParams?: ScheduleListParams
  options?: Omit<UseQueryOptions<PaginatedScheduleList, ApiError>, 'queryKey'>
}

export const useListSchedules = ({
  queryParams = {},
  options,
}: UseListSchedulesParams = {}) => {
  const api = useScheduleApi()

  const query = useQuery<PaginatedScheduleList, ApiError>({
    queryKey: SCHEDULE_QUERY_KEYS.list(queryParams),
    queryFn: async () => {
      const { ok, data } = await api.getSchedules(queryParams)

      if (!ok || !data) {
        return Promise.reject({
          code: 'fetch_schedules_error',
          message: 'Failed to fetch schedules',
        })
      }

      return data
    },
    ...options,
    retry: options?.retry ?? false,
  })

  return {
    ...query,
    schedules: query.data?.results ?? [],
    pagination: {
      count: query.data?.count ?? 0,
      next: query.data?.next,
      previous: query.data?.previous,
    },
  }
}

// Get Single Schedule Hook
interface UseGetScheduleParams {
  id: ScheduleId
  options?: Omit<UseQueryOptions<ScheduleRetrieve, ApiError>, 'queryKey'>
}

export const useGetSchedule = ({ id, options }: UseGetScheduleParams) => {
  const api = useScheduleApi()

  return useQuery<ScheduleRetrieve, ApiError>({
    queryKey: SCHEDULE_QUERY_KEYS.detail(id),
    queryFn: async () => {
      const { ok, data } = await api.getSchedule(id)

      if (!ok || !data) {
        return Promise.reject({
          code: 'fetch_schedule_error',
          message: `Failed to fetch schedule ${id}`,
        })
      }

      return data
    },
    enabled: !!id,
    ...options,
    retry: options?.retry ?? false,
  })
}

// Get Schedule Events Hook
interface UseGetScheduleEventsParams {
  scheduleId: ScheduleId
  options?: Omit<UseQueryOptions<ScheduleEvent[], ApiError>, 'queryKey'>
}

export const useGetScheduleEvents = ({
  scheduleId,
  options,
}: UseGetScheduleEventsParams) => {
  const api = useScheduleApi()

  return useQuery<ScheduleEvent[], ApiError>({
    queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
    queryFn: async () => {
      const { ok, data } = await api.getScheduleEvents(scheduleId)

      if (!ok || !data) {
        return Promise.reject({
          code: 'fetch_events_error',
          message: `Failed to fetch events for schedule ${scheduleId}`,
        })
      }

      return data
    },
    enabled: !!scheduleId,
    ...options,
    retry: options?.retry ?? false,
  })
}

// Get Schedule Holidays Hook (preset holiday templates)
interface UseGetScheduleHolidaysParams {
  options?: Omit<UseQueryOptions<ScheduleHoliday[], ApiError>, 'queryKey'>
}

export const useGetScheduleHolidays = ({
  options,
}: UseGetScheduleHolidaysParams = {}) => {
  const api = useScheduleApi()

  return useQuery<ScheduleHoliday[], ApiError>({
    queryKey: SCHEDULE_QUERY_KEYS.holidays(),
    queryFn: async () => {
      const { ok, data } = await api.getScheduleHolidays()

      if (!ok || !data) {
        return Promise.reject({
          code: 'fetch_holidays_error',
          message: 'Failed to fetch schedule holidays',
        })
      }

      return data
    },
    ...options,
    retry: options?.retry ?? false,
  })
}

// Create Schedule Mutation
interface CreateScheduleRequest {
  data: ScheduleCreate
  defaultWeeklyHours?: boolean
}

interface UseCreateScheduleParams {
  options?: Omit<
    UseMutationOptions<Schedule, ApiError, CreateScheduleRequest>,
    'mutationFn'
  >
}

export const useCreateSchedule = ({
  options,
}: UseCreateScheduleParams = {}) => {
  const api = useScheduleApi()
  const queryClient = useQueryClient()
  const {
    onSuccess: optionsOnSuccess,
    onError: optionsOnError,
    ...restOptions
  } = options || {}

  return useMutation<Schedule, ApiError, CreateScheduleRequest>({
    mutationFn: async (request: CreateScheduleRequest) => {
      const { ok, data } = await api.createSchedule(request.data, {
        defaultWeeklyHours: request.defaultWeeklyHours,
      })

      if (!ok || !data) {
        return Promise.reject({
          code: 'create_schedule_error',
          message: 'Failed to create schedule',
        })
      }

      return data
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: SCHEDULE_QUERY_KEYS.lists() })
      optionsOnSuccess?.(...args)
    },
    onError: (...args) => {
      optionsOnError?.(...args)
    },
    ...restOptions,
  })
}

// Update Schedule Mutation
interface UseUpdateScheduleParams {
  invalidateQueries?: boolean
  options?: Omit<
    UseMutationOptions<
      Schedule,
      ApiError,
      { id: ScheduleId; data: ScheduleUpdate }
    >,
    'mutationFn'
  >
}

export const useUpdateSchedule = ({
  invalidateQueries = true,
  options,
}: UseUpdateScheduleParams = {}) => {
  const api = useScheduleApi()
  const queryClient = useQueryClient()
  const {
    onSuccess: optionsOnSuccess,
    onError: optionsOnError,
    ...restOptions
  } = options || {}

  return useMutation<
    Schedule,
    ApiError,
    { id: ScheduleId; data: ScheduleUpdate }
  >({
    mutationFn: async ({ id, data }) => {
      const { ok, data: responseData } = await api.updateSchedule(id, data)

      if (!ok || !responseData) {
        return Promise.reject({
          code: 'update_schedule_error',
          message: `Failed to update schedule ${id}`,
        })
      }

      return responseData
    },
    onSuccess: (...args) => {
      const [, variables] = args
      if (invalidateQueries) {
        queryClient.invalidateQueries({
          queryKey: SCHEDULE_QUERY_KEYS.detail(variables.id),
        })
        queryClient.invalidateQueries({
          queryKey: SCHEDULE_QUERY_KEYS.lists(),
        })
      }
      optionsOnSuccess?.(...args)
    },
    onError: (...args) => {
      optionsOnError?.(...args)
    },
    ...restOptions,
  })
}

// Delete Schedule Mutation
interface UseDeleteScheduleParams {
  options?: Omit<UseMutationOptions<void, ApiError, ScheduleId>, 'mutationFn'>
}

export const useDeleteSchedule = ({
  options,
}: UseDeleteScheduleParams = {}) => {
  const api = useScheduleApi()
  const queryClient = useQueryClient()
  const {
    onSuccess: optionsOnSuccess,
    onError: optionsOnError,
    ...restOptions
  } = options || {}

  return useMutation<void, ApiError, ScheduleId>({
    mutationFn: async (id: ScheduleId) => {
      const { ok, data } = await api.deleteSchedule(id)

      if (!ok) {
        return Promise.reject(buildDeleteScheduleError(id, data))
      }
    },
    onSuccess: (...args) => {
      const [, scheduleId] = args

      // Remove deleted schedule from cached lists immediately for responsive UI.
      queryClient.setQueriesData<PaginatedScheduleList>(
        { queryKey: SCHEDULE_QUERY_KEYS.lists() },
        (current) => {
          if (!current) return current

          const hadSchedule = current.results.some((s) => s.id === scheduleId)
          if (!hadSchedule) return current

          return {
            ...current,
            count: Math.max(0, current.count - 1),
            results: current.results.filter((s) => s.id !== scheduleId),
          }
        },
      )

      queryClient.invalidateQueries({ queryKey: SCHEDULE_QUERY_KEYS.lists() })
      queryClient.removeQueries({
        queryKey: SCHEDULE_QUERY_KEYS.detail(scheduleId),
        // Removing an actively observed detail query causes React Query to
        // recreate it before the detail page finishes navigating away. Keep
        // active data until unmount so a successful DELETE is not followed by
        // an expected-but-noisy GET 404; inactive detail caches are still
        // discarded immediately (for example, deletion from the list page).
        type: 'inactive',
      })
      optionsOnSuccess?.(...args)
    },
    onError: (...args) => {
      optionsOnError?.(...args)
    },
    ...restOptions,
  })
}

// Create Schedule Event Mutation
// Directly adds the new event to cache on success to avoid refetch flicker
interface UseCreateScheduleEventParams {
  scheduleId: ScheduleId
  options?: Omit<
    UseMutationOptions<ScheduleEvent, ApiError, ScheduleEventCreate>,
    'mutationFn'
  >
}

export const useCreateScheduleEvent = ({
  scheduleId,
  options,
}: UseCreateScheduleEventParams) => {
  const api = useScheduleApi()
  const queryClient = useQueryClient()
  const {
    onSuccess: optionsOnSuccess,
    onError: optionsOnError,
    ...restOptions
  } = options || {}

  return useMutation<ScheduleEvent, ApiError, ScheduleEventCreate>({
    mutationFn: async (request: ScheduleEventCreate) => {
      const { ok, data } = await api.createScheduleEvent(scheduleId, request)

      if (!ok || !data) {
        return Promise.reject({
          code: 'create_event_error',
          message: 'Failed to create schedule event',
        })
      }

      return data
    },
    onSuccess: (...args) => {
      const [newEvent, request] = args
      // Directly add the new event to the cache instead of refetching
      queryClient.setQueryData<ScheduleEvent[]>(
        SCHEDULE_QUERY_KEYS.events(scheduleId),
        (old) => [...(old || []), { ...request, ...newEvent }],
      )
      queryClient.invalidateQueries({
        queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
      })
      queryClient.invalidateQueries({
        queryKey: SCHEDULE_QUERY_KEYS.detail(scheduleId),
      })
      // Call user-provided onSuccess if it exists
      optionsOnSuccess?.(...args)
    },
    onError: (...args) => {
      queryClient.invalidateQueries({
        queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
      })
      optionsOnError?.(...args)
    },
    ...restOptions,
  })
}

// Update Schedule Event Mutation
// Directly updates the event in cache on success to avoid refetch flicker
interface UseUpdateScheduleEventParams {
  scheduleId: ScheduleId
  invalidateQueries?: boolean
  options?: Omit<
    UseMutationOptions<
      ScheduleEvent,
      ApiError,
      { eventId: ScheduleEventId; data: ScheduleEventUpdate }
    >,
    'mutationFn'
  >
}

export const useUpdateScheduleEvent = ({
  scheduleId,
  invalidateQueries = true,
  options,
}: UseUpdateScheduleEventParams) => {
  const api = useScheduleApi()
  const queryClient = useQueryClient()
  const {
    onSuccess: optionsOnSuccess,
    onError: optionsOnError,
    ...restOptions
  } = options || {}

  return useMutation<
    ScheduleEvent,
    ApiError,
    { eventId: ScheduleEventId; data: ScheduleEventUpdate }
  >({
    mutationFn: async ({ eventId, data }) => {
      const { ok, data: responseData } = await api.updateScheduleEvent(
        scheduleId,
        eventId,
        data,
      )

      if (!ok || !responseData) {
        return Promise.reject({
          code: 'update_event_error',
          message: `Failed to update schedule event ${eventId}`,
        })
      }

      return responseData
    },
    onSuccess: (...args) => {
      const [updatedEvent, variables] = args
      // Directly update the event in cache instead of refetching
      queryClient.setQueryData<ScheduleEvent[]>(
        SCHEDULE_QUERY_KEYS.events(scheduleId),
        (old) =>
          old?.map((event) =>
            event.id === variables.eventId
              ? { ...event, ...updatedEvent, ...variables.data }
              : event,
          ),
      )
      if (invalidateQueries) {
        queryClient.invalidateQueries({
          queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
        })
        queryClient.invalidateQueries({
          queryKey: SCHEDULE_QUERY_KEYS.detail(scheduleId),
        })
      }
      // Call user-provided onSuccess if it exists
      optionsOnSuccess?.(...args)
    },
    onError: (...args) => {
      if (invalidateQueries) {
        queryClient.invalidateQueries({
          queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
        })
      }
      optionsOnError?.(...args)
    },
    ...restOptions,
  })
}

// Delete Schedule Event Mutation (with optimistic update)
interface UseDeleteScheduleEventParams {
  scheduleId: ScheduleId
  options?: Omit<
    UseMutationOptions<
      void,
      ApiError,
      ScheduleEventId,
      { previousEvents: ScheduleEvent[] | undefined }
    >,
    'mutationFn'
  >
}

export const useDeleteScheduleEvent = ({
  scheduleId,
  options,
}: UseDeleteScheduleEventParams) => {
  const api = useScheduleApi()
  const queryClient = useQueryClient()
  const {
    onSuccess: optionsOnSuccess,
    onError: optionsOnError,
    ...restOptions
  } = options || {}

  return useMutation<
    void,
    ApiError,
    ScheduleEventId,
    { previousEvents: ScheduleEvent[] | undefined }
  >({
    mutationFn: async (eventId: ScheduleEventId) => {
      const { ok } = await api.deleteScheduleEvent(scheduleId, eventId)

      if (!ok) {
        return Promise.reject({
          code: 'delete_event_error',
          message: `Failed to delete schedule event ${eventId}`,
        })
      }
    },
    // Optimistically remove from cache immediately
    onMutate: async (eventId) => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({
        queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
      })

      // Snapshot previous value for rollback
      const previousEvents = queryClient.getQueryData<ScheduleEvent[]>(
        SCHEDULE_QUERY_KEYS.events(scheduleId),
      )

      // Optimistically remove from cache
      queryClient.setQueryData<ScheduleEvent[]>(
        SCHEDULE_QUERY_KEYS.events(scheduleId),
        (old) => old?.filter((event) => event.id !== eventId),
      )

      return { previousEvents }
    },
    // Rollback on error and refetch
    onError: (...args) => {
      const [, , context] = args
      if (context?.previousEvents) {
        queryClient.setQueryData(
          SCHEDULE_QUERY_KEYS.events(scheduleId),
          context.previousEvents,
        )
      }
      // Refetch to ensure consistency after error
      queryClient.invalidateQueries({
        queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
      })
      // Call user-provided onError if it exists
      optionsOnError?.(...args)
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: SCHEDULE_QUERY_KEYS.events(scheduleId),
      })
      queryClient.invalidateQueries({
        queryKey: SCHEDULE_QUERY_KEYS.detail(scheduleId),
      })
      // Call user-provided onSuccess if it exists
      optionsOnSuccess?.(...args)
    },
    ...restOptions,
  })
}
